/**
 * Home / specialization / workshop state — checklist items 206-208, 236 and
 * 237, which are one panel and therefore one store (see `home-state.types.ts`).
 *
 * Follows `shop-state.ts`'s `createStore` + fetch shape: one `busy` flag, one
 * `error` string, every write refreshing what it invalidated.
 *
 * ─── What refreshes what, and why it is not a preference ────────────────────
 * The three reads are coupled in one direction each, and getting the coupling
 * wrong shows up as a panel that lies rather than as an error:
 *
 *   upgrade       → home (the level moved) AND bench (a rung can unlock
 *                   recipes — that is the entire point of item 237) AND the
 *                   inventory sheet (materials left the hideout)
 *   choose a spec → catalog (`active` moved) AND bench (the recipe POOL is a
 *                   different set of recipes now) AND home (the Zone row that
 *                   `GET /home` reads was created by the choice)
 *   craft/collect → bench AND the inventory sheet
 *
 * ─── The inventory refresh is NOT made here ─────────────────────────────────
 * Every write above moves items in or out of the hideout, and the paperdoll and
 * the HUD load bar read `gameStateStore.inventory` — a craft that quietly took
 * four cement bags while the open inventory still shows them is the kind of bug
 * a player reports as "it stole my materials".
 *
 * It is already handled, and re-fetching here would be the second mechanism.
 * `HomeService.upgrade` and `CraftingService.start`/`collect` all call
 * `InventoryService.refreshAndEmit` after their transaction commits;
 * `GameGateway` forwards that as `inventory:updated`, and `game-state.ts` has
 * subscribed to it since S9. The push carries the sheet the server actually
 * wrote, which a follow-up GET can only approximate — and none of these three
 * endpoints returns a sheet in its response body the way the vendor's do.
 *
 * The consequence to know: with the socket down, the hideout list goes stale
 * until the next `loadGameState()`. That is the same exposure every other
 * push-fed readout in this client already has, not a new one.
 */
import { createStore } from '../../core/state/store';
import { api } from '../../core/network/api';
import type {
  CraftBench, HomeKeyOffer, HomeState, HomeTab, HomeView, SpecializationCatalog,
} from './home-state.types';

const INITIAL_STATE: HomeState = {
  home: null,
  catalog: null,
  bench: null,
  tab: 'HOUSE',
  busy: false,
  error: null,
  notice: null,
  confirmUpgrade: false,
  inspecting: null,
  changingSpec: false,
  homeKey: null,
  confirmHomeKey: false,
  confirmSkipJobId: null,
};

export const homeStateStore = createStore<HomeState>({ ...INITIAL_STATE });

// ─── Reads ──────────────────────────────────────────────────────────────────

/**
 * Loads the whole panel.
 *
 * The bench is fetched with `catch` rather than as a hard failure: `GET
 * /crafting` throws `NO_ZONE` for a player who has not chosen a specialization yet,
 * which is the NORMAL state of the panel the very first time it opens. Letting
 * that reject would paint an error over a house the player can perfectly well
 * upgrade.
 */
export async function loadHome(): Promise<void> {
  homeStateStore.set((s) => ({ ...s, busy: true, error: null }));
  try {
    const [home, catalog, bench, homeKey] = await Promise.all([
      api.get<HomeView>('/home'),
      api.get<SpecializationCatalog>('/zone/specializations'),
      api.get<CraftBench>('/crafting').catch(() => null),
      // Same tolerance as the bench: the offer is a nicety, and a shop that is
      // down must not hide a house the player can still upgrade.
      api.get<HomeKeyOffer>('/shop/premium/home-key').catch(() => null),
    ]);
    homeStateStore.set((s) => ({
      ...s, home, catalog, bench, homeKey, busy: false,
    }));
  } catch (err) {
    homeStateStore.set((s) => ({ ...s, busy: false, error: msg(err, 'Couldn’t reach your home.') }));
  }
}

/**
 * Re-reads only `GET /home`, quietly — no `busy`, no cleared error.
 *
 * The House tab's material cards count the hideout AND the pack, so they go
 * stale the moment either changes. The panel calls this on every
 * `inventory:updated` push while it is open; a failure keeps the last numbers
 * rather than painting an error over a panel that is otherwise fine.
 */
export async function refreshHomeView(): Promise<void> {
  try {
    const home = await api.get<HomeView>('/home');
    homeStateStore.set((s) => ({ ...s, home }));
  } catch {
    // Keep what is on screen; the next push or reopen tries again.
  }
}

/** Re-reads only the bench — after a craft, a collect, or a spec change. */
async function refreshBench(): Promise<void> {
  const bench = await api.get<CraftBench>('/crafting').catch(() => null);
  homeStateStore.set((s) => ({ ...s, bench }));
}

// ─── Writes ─────────────────────────────────────────────────────────────────

/**
 * Raises the home one level (checklist 208).
 *
 * Takes no level argument, matching `POST /home/upgrade` — "upgrade" always
 * means the next rung, and a level sent from the client is a number the server
 * has to re-derive anyway.
 */
export async function upgradeHome(): Promise<void> {
  homeStateStore.set((s) => ({
    ...s, busy: true, error: null, notice: null,
  }));
  try {
    const result = await api.post<{ home: HomeView; fromLevel: number; toLevel: number }>('/home/upgrade');
    homeStateStore.set((s) => ({
      ...s,
      home: result.home,
      busy: false,
      confirmUpgrade: false,
      notice: `Home raised to level ${result.toLevel}.`,
    }));
    // A rung can unlock recipes and craft slots — that is the whole point of
    // item 237, and the bench is the only place it shows.
    await refreshBench();
  } catch (err) {
    homeStateStore.set((s) => ({
      ...s, busy: false, error: msg(err, 'The upgrade didn’t go through.'),
    }));
  }
}

/**
 * Commits to a vocation (checklist 236).
 *
 * `confirmLoss` is forwarded rather than always sent: the server rejects a
 * switch that would discard uncollected crafts with `SWITCH_WOULD_DISCARD_JOBS`
 * unless it is set, and that rejection is the only thing standing between a
 * player and a silently binned two-hour craft. The panel asks, then re-sends.
 */
export async function chooseSpecialization(
  specialization: string,
  opts: { confirmLoss?: boolean } = {},
): Promise<void> {
  homeStateStore.set((s) => ({
    ...s, busy: true, error: null, notice: null,
  }));
  try {
    await api.post('/zone/specialization', { specialization, ...opts });
    const [catalog, home] = await Promise.all([
      api.get<SpecializationCatalog>('/zone/specializations'),
      // The Zone row that `GET /home` reads may have been CREATED by this call.
      api.get<HomeView>('/home'),
    ]);
    const name = catalog.options.find((o) => o.key === specialization)?.name ?? specialization;
    homeStateStore.set((s) => ({
      ...s, catalog, home, busy: false, inspecting: null, changingSpec: false, notice: `You are a ${name} now.`,
    }));
    // The recipe POOL is a different set of recipes now, not a longer one.
    await refreshBench();
  } catch (err) {
    homeStateStore.set((s) => ({
      ...s, busy: false, error: msg(err, 'Couldn’t commit to that specialization.'),
    }));
  }
}

/**
 * Buys the home outright with Gold (checklist 207), skipping the Overseer's
 * quest.
 *
 * Re-reads the offer as well as the home: `owned` is what kills the button, and
 * leaving it stale would leave a live "buy" on a house the player now owns.
 */
export async function buyHomeKey(): Promise<void> {
  homeStateStore.set((s) => ({
    ...s, busy: true, error: null, notice: null,
  }));
  try {
    const result = await api.post<{ home: HomeView; goldSpent: number; offer: HomeKeyOffer }>('/shop/premium/home-key');
    homeStateStore.set((s) => ({
      ...s,
      home: result.home,
      homeKey: result.offer,
      busy: false,
      confirmHomeKey: false,
      notice: `The plot is yours — ${result.goldSpent} Gold.`,
    }));
  } catch (err) {
    homeStateStore.set((s) => ({
      ...s, busy: false, confirmHomeKey: false, error: msg(err, 'Couldn’t buy the key.'),
    }));
  }
}

/**
 * Pays Gold to finish a craft now.
 *
 * Sends no price. `POST /crafting/:jobId/skip` takes no body on purpose — the
 * cost is a function of the hours still to run, which only the server can read
 * honestly, and accepting a quoted number would be accepting a number the
 * player types. The button's displayed price is a preview computed from the
 * same formula, not a quote.
 */
export async function skipCraft(jobId: string): Promise<void> {
  homeStateStore.set((s) => ({
    ...s, busy: true, error: null, notice: null,
  }));
  try {
    const result = await api.post<{ goldSpent: number }>(`/crafting/${jobId}/skip`);
    homeStateStore.set((s) => ({
      ...s,
      busy: false,
      confirmSkipJobId: null,
      // `goldSpent: 0` is the server's idempotent no-op — the craft was
      // already ready. Saying "paid 0 Gold" would read as a bug.
      notice: result.goldSpent > 0 ? `Finished early for ${result.goldSpent} Gold.` : 'That one was already done.',
    }));
    await refreshBench();
  } catch (err) {
    homeStateStore.set((s) => ({
      ...s, busy: false, confirmSkipJobId: null, error: msg(err, 'Couldn’t finish that early.'),
    }));
  }
}

/** Queues a craft (checklist 237). Materials and the Cash fee are taken at START. */
export async function startCraft(recipeKey: string): Promise<void> {
  homeStateStore.set((s) => ({
    ...s, busy: true, error: null, notice: null,
  }));
  try {
    await api.post('/crafting/start', { recipeKey });
    homeStateStore.set((s) => ({ ...s, busy: false, notice: 'Work started.' }));
    await refreshBench();
  } catch (err) {
    homeStateStore.set((s) => ({ ...s, busy: false, error: msg(err, 'Couldn’t start that.') }));
  }
}

/** Collects a finished craft into the hideout. */
export async function collectCraft(jobId: string): Promise<void> {
  homeStateStore.set((s) => ({
    ...s, busy: true, error: null, notice: null,
  }));
  try {
    const result = await api.post<{ output: { itemKey: string; qty: number } }>(`/crafting/${jobId}/collect`);
    homeStateStore.set((s) => ({
      ...s, busy: false, notice: `Collected ${result.output.qty}x ${result.output.itemKey}.`,
    }));
    await refreshBench();
  } catch (err) {
    homeStateStore.set((s) => ({ ...s, busy: false, error: msg(err, 'Couldn’t collect that.') }));
  }
}

// ─── Panel-local UI state ───────────────────────────────────────────────────

export function setHomeTab(tab: HomeTab): void {
  homeStateStore.set((s) => ({
    ...s, tab, error: null, notice: null,
  }));
}

export function setUpgradeConfirm(open: boolean): void {
  homeStateStore.set((s) => ({ ...s, confirmUpgrade: open, error: null }));
}

export function setHomeKeyConfirm(open: boolean): void {
  homeStateStore.set((s) => ({ ...s, confirmHomeKey: open, error: null }));
}

/** Opens the "finish it now" confirmation for one job. `null` closes it. */
export function setSkipConfirm(jobId: string | null): void {
  homeStateStore.set((s) => ({ ...s, confirmSkipJobId: jobId, error: null }));
}

/** Expands one vocation's detail in the chooser. `null` collapses. */
export function inspectSpecialization(key: string | null): void {
  homeStateStore.set((s) => ({ ...s, inspecting: key }));
}

/** Shows (true) or hides (false) the chooser on the Workshop tab once a specialization is picked. */
export function setChangingSpec(on: boolean): void {
  homeStateStore.set((s) => ({ ...s, changingSpec: on, inspecting: null, error: null }));
}

/**
 * What closing the panel clears: its tab, prompts and messages. Never the
 * loaded house — `base-view/index.ts` reads `home.owned` for the action-bar
 * button, the H key and the front door, and `null` reads as unowned, so a full
 * reset here greyed out the Home button until the next `loadHome()`.
 */
export function resetHomePanelState(): void {
  homeStateStore.set((s) => ({
    ...INITIAL_STATE, home: s.home, catalog: s.catalog, bench: s.bench, homeKey: s.homeKey,
  }));
}

export function resetHomeState(): void {
  homeStateStore.set({ ...INITIAL_STATE });
}

// ─── Derived ────────────────────────────────────────────────────────────────

/**
 * Should the panel open on the chooser rather than on the house?
 *
 * Expressed as a predicate over the loaded state rather than as a one-shot
 * event, so it survives a reload, a remount and the player closing the panel
 * and coming back. True when the home is owned (the server reports
 * `canChoose` only then) and NO specialization has been chosen yet — and
 * therefore false for ever after the first choice, so it cannot nag.
 *
 * There is no level or quest condition: the player chooses freely. (Until
 * 2026-10-19 this also required house level >= 2, mirroring the level gate.)
 */
export function shouldPromptForSpecialization(state: HomeState): boolean {
  const { home, catalog } = state;
  if (!home || !catalog) return false;
  if (catalog.zone.specialization !== null) return false;
  return catalog.zone.canChoose;
}

function msg(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}
